/**
 * Utility functions for handling ICD codes
 */

/**
 * Parse ICD codes from various formats (JSON string, comma-separated string, or array)
 * @param {string|array} icdCodes - The ICD codes to parse
 * @returns {array} Array of ICD codes
 */
export const parseIcdCodes = (icdCodes) => {
  if (!icdCodes) return [];
  
  if (Array.isArray(icdCodes)) {
    return icdCodes;
  }
  
  if (typeof icdCodes === 'string') {
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(icdCodes);
      return Array.isArray(parsed) ? parsed : [icdCodes];
    } catch (e) {
      // If JSON parsing fails, treat as comma-separated string
      if (icdCodes.includes(',')) {
        return icdCodes.split(',').map(code => code.trim()).filter(code => code);
      }
      // Single code
      return icdCodes.trim() ? [icdCodes.trim()] : [];
    }
  }
  
  return [];
};

/**
 * Format ICD codes for display
 * @param {string|array} icdCodes - The ICD codes to format
 * @returns {string} Formatted string for display
 */
export const formatIcdCodes = (icdCodes) => {
  const codes = parseIcdCodes(icdCodes);
  return codes.join(', ');
};

/**
 * Validate ICD codes format
 * @param {string|array} icdCodes - The ICD codes to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateIcdCodes = (icdCodes) => {
  const codes = parseIcdCodes(icdCodes);
  return codes.length > 0 && codes.every(code => typeof code === 'string' && code.trim());
};
